import { useEffect, useState } from 'react'
import { Outlet, useNavigate, useLocation } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useLangParam, type SupportedLang } from '@/shared/hooks'
import { useAuth } from '@/features/auth/hooks/use-auth'
import { supabase } from '@/shared/lib/supabase'

/**
 * Layout component that synchronizes i18n language with URL.
 *
 * This component:
 * 1. Extracts language from URL param `:lang`
 * 2. Validates the language (redirects to /en if invalid)
 * 3. Syncs i18n with the URL language
 * 4. Updates `<html lang="...">` attribute
 * 5. Redirects to user's preferred locale if logged in (profile priority)
 *
 * Should be used as a layout wrapper for all routes in router.tsx:
 * ```tsx
 * {
 *   path: '/:lang',
 *   element: <LanguageLayout />,
 *   children: [...]
 * }
 * ```
 */
export function LanguageLayout() {
  const { lang, isValidLang } = useLangParam()
  const { i18n } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { user, isLoading: isAuthLoading } = useAuth()

  // State to track user's preferred locale from database
  const [userLocale, setUserLocale] = useState<SupportedLang | null>(null)
  const [isLocaleLoading, setIsLocaleLoading] = useState(false)
  const [hasCheckedLocale, setHasCheckedLocale] = useState(false)

  // Fetch user's preferred locale from database when authenticated
  useEffect(() => {
    async function fetchUserLocale() {
      if (!user) {
        setUserLocale(null)
        setHasCheckedLocale(true)
        return
      }

      setIsLocaleLoading(true)
      try {
        const { data } = await supabase
          .from('users')
          .select('locale')
          .eq('id', user.id)
          .single()

        if (data?.locale) {
          setUserLocale(data.locale as SupportedLang)
        }
      } catch {
        // Ignore errors, fallback to URL language
      } finally {
        setIsLocaleLoading(false)
        setHasCheckedLocale(true)
      }
    }

    if (!isAuthLoading) {
      fetchUserLocale()
    }
  }, [user, isAuthLoading])

  // Redirect if language in URL is invalid
  useEffect(() => {
    if (!isValidLang) {
      // Replace invalid lang prefix with 'en'
      const pathWithoutLang = location.pathname.replace(/^\/[^/]+/, '')
      const newPath = `/en${pathWithoutLang || ''}`
      navigate(newPath + location.search, { replace: true })
    }
  }, [isValidLang, location.pathname, location.search, navigate])

  // Redirect to user's preferred locale if different from URL
  // Only runs after we've checked the user's locale to avoid race conditions
  useEffect(() => {
    if (
      !isAuthLoading &&
      !isLocaleLoading &&
      hasCheckedLocale &&
      userLocale &&
      userLocale !== lang
    ) {
      // Replace current lang prefix with user's preferred locale
      const pathWithoutLang = location.pathname.replace(/^\/[^/]+/, '')
      const newPath = `/${userLocale}${pathWithoutLang || ''}`
      navigate(newPath + location.search, { replace: true })
    }
  }, [
    userLocale,
    lang,
    isAuthLoading,
    isLocaleLoading,
    hasCheckedLocale,
    location.pathname,
    location.search,
    navigate,
  ])

  // Synchronize i18n language with URL
  useEffect(() => {
    if (isValidLang && i18n.language !== lang) {
      i18n.changeLanguage(lang)
    }
  }, [lang, isValidLang, i18n])

  // Update <html lang="..."> attribute
  useEffect(() => {
    if (isValidLang) {
      document.documentElement.lang = lang
    }
  }, [lang, isValidLang])

  // Show nothing while redirecting (invalid lang or locale mismatch)
  if (!isValidLang) {
    return null
  }

  // Wait for locale check before rendering to prevent flash
  if (user && !hasCheckedLocale) {
    return null
  }

  return <Outlet />
}
